<template>
  <!-- CustomizeColumnMenu: Reusable column visibility customization component -->
  <v-menu
    v-model="isMenuOpen"
    :close-on-content-click="false"
    offset-y
    transition="slide-y-transition"
    :data-testid="`${dataTestidPrefix}__customize-menu`"
  >
    <!-- Menu Activator Button -->
    <template v-slot:activator="{ on, attrs }">
      <v-tooltip left content-class="tooltip-left">
        <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-btn
            icon
            v-bind="{ ...attrs, ...tooltipAttrs }"
            v-on="{ ...on, ...tooltipOn }"
            :data-testid="`${dataTestidPrefix}__customize-btn`"
          >
            <img src="/img/icons/fbc-icons/CustomizeColumn.svg" alt="Customize Column" :data-testid="`${dataTestidPrefix}__customize-icon`" />
          </v-btn>
        </template>
        <span>Customize column</span>
      </v-tooltip>
    </template>

    <!-- Menu Content -->
    <v-list
      class="customize-column-menu__list pa-0 ma-0 d-flex flex-column align-start justify-start all-width"
      :data-testid="`${dataTestidPrefix}__customize-list`"
    >
      <!-- Header Section -->
      <v-list-item class="ma-0 all-width pa-0" :data-testid="`${dataTestidPrefix}__customize-header`">
        <div class="d-flex tableHeaderBg align-center pa-2 pt-3 pb-3 justify-space-between all-width">
          <p class="ma-0 font-weight-bold" :data-testid="`${dataTestidPrefix}__customize-title`">
            Customize Column
          </p>
          <v-tooltip v-if="showHeaderInfoIcon" bottom content-class="tooltip-bottom">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                :color="color"
                small
                class="customize-column-menu__info-icon"
                :data-testid="`${dataTestidPrefix}__customize-header-info-icon`"
              >
                mdi-information-outline
              </v-icon>
            </template>
            <span>Select which columns to display in the table</span>
          </v-tooltip>
        </div>
      </v-list-item>

      <v-divider class="all-width" :data-testid="`${dataTestidPrefix}__customize-divider-top`" />

      <!-- Search Input -->
      <v-list-item class="all-width pa-0 px-2 mt-3 mb-2" :data-testid="`${dataTestidPrefix}__customize-search-container`">
        <lb-string
          label=""
          placeholder="Search"
          v-model="searchTerm"
          width="100%"
          :isClearable="true"
          appendiconinner="mdi-magnify"
          hidedetails
          radius="8px"
          :data-testid="`${dataTestidPrefix}__customize-search`"
        />
      </v-list-item>

      <!-- Select All Toggle Row (hidden when search is active to avoid confusion) -->
      <div 
        v-if="!isSearchActive"
        class="all-width px-2 py-1 d-flex align-center"
        :data-testid="`${dataTestidPrefix}__customize-select-actions`"
      >
        <v-btn
          text
          small
          :color="color"
          class="text-none px-2"
          @click.stop="toggleSelectAll"
          :data-testid="`${dataTestidPrefix}__customize-select-all-btn`"
        >
          <v-icon small class="mr-1">{{ selectAllIcon }}</v-icon>
          <span>{{ selectAllLabel }}</span>
        </v-btn>
      </div>

      <!-- Column Checkboxes List -->
      <v-list-item
        class="customize-column-menu__items-container d-flex all-width align-start pa-0 ma-0 flex-column"
        :data-testid="`${dataTestidPrefix}__customize-items-container`"
      >
        <template v-for="(item, index) in filteredHeaders">
          <v-list-item
            v-if="!isExcludedColumn(item)"
            :key="`column-${item.value}-${index}`"
            class="d-flex pa-0 px-2 ma-0"
            :data-testid="`${dataTestidPrefix}__customize-item-${item.value || index}`"
          >
            <v-checkbox
              :color="color"
              :ripple="false"
              class="pa-0 ma-0"
              @change="toggleColumnVisibility(item)"
              hide-details
              :input-value="getItemVisibility(item)"
              :data-testid="`${dataTestidPrefix}__customize-checkbox-${item.value || index}`"
            >
              <template v-slot:label>
                <span :data-testid="`${dataTestidPrefix}__customize-label-${item.value || index}`">
                  {{ item.text }}
                </span>
              </template>
            </v-checkbox>
          </v-list-item>
        </template>
      </v-list-item>

      <v-divider class="all-width" :data-testid="`${dataTestidPrefix}__customize-divider-bottom`" />

      <!-- Action Buttons -->
      <v-list-item
        v-if="showApplyButton || showSaveButton"
        class="d-flex align-center justify-end all-width px-2 py-2"
        :data-testid="`${dataTestidPrefix}__customize-actions`"
      >
        <v-spacer />
        
        <!-- Apply Button -->
        <v-btn
          v-if="showApplyButton"
          outlined
          small
          :color="color"
          class="mr-2 text-none"
          @click="handleApply"
          :data-testid="`${dataTestidPrefix}__customize-apply-btn`"
        >
          <span :data-testid="`${dataTestidPrefix}__customize-apply-label`">Apply</span>
        </v-btn>

        <!-- Save Preferences Button -->
        <v-btn
          v-if="showSaveButton"
          small
          :color="color"
          :loading="loading"
          :disabled="loading"
          class="white--text text-none"
          @click="handleSave"
          :data-testid="`${dataTestidPrefix}__customize-save-btn`"
        >
          <span :data-testid="`${dataTestidPrefix}__customize-save-label`">Save Layout</span>
        </v-btn>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
/**
 * CustomizeColumnMenu Component
 * 
 * A reusable component for customizing table column visibility.
 * Supports both temporary (Apply) and persistent (Save) column preferences.
 * 
 * @example
 * <CustomizeColumnMenu
 *   v-model="menuOpen"
 *   :headers="tableHeaders"
 *   :page-name="page_name"
 *   @apply="onApplyColumns"
 *   @save="onSaveColumns"
 *   data-testid-prefix="tasklist"
 * />
 */
export default {
  name: 'CustomizeColumnMenu',

  props: {
    /**
     * Controls the menu open state (v-model)
     */
    value: {
      type: Boolean,
      default: false,
    },

    /**
     * Array of header objects with { text, value, visible } structure
     */
    headers: {
      type: Array,
      required: true,
      validator: (headers) => {
        return headers.every(h => 
          typeof h.text === 'string' && 
          typeof h.value !== 'undefined'
        );
      },
    },

    /**
     * Column values to exclude from the customization list
     */
    excludeColumns: {
      type: Array,
      default: () => ['action'],
    },

    /**
     * Theme color for checkboxes, buttons, and icons
     */
    color: {
      type: String,
      default: 'caccounting',
    },

    /**
     * Whether to show the Apply button
     */
    showApplyButton: {
      type: Boolean,
      default: true,
    },

    /**
     * Whether to show the Save Columns button
     */
    showSaveButton: {
      type: Boolean,
      default: true,
    },

    /**
     * Whether to show info icon next to the header title
     */
    showHeaderInfoIcon: {
      type: Boolean,
      default: false,
    },

    /**
     * Page identifier for API persistence
     */
    pageName: {
      type: String,
      default: '',
    },

    /**
     * Prefix for all data-testid attributes
     */
    dataTestidPrefix: {
      type: String,
      default: 'customize-column',
    },

    /**
     * Loading state for save button
     */
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Search term for filtering columns
      searchTerm: '',
      // Internal copy of headers to track pending changes
      pendingHeaders: [],
    };
  },

  computed: {
    /**
     * Two-way binding for menu open state
     */
    isMenuOpen: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    /**
     * Check if search is currently active
     */
    isSearchActive() {
      return this.searchTerm && this.searchTerm.trim() !== '';
    },

    /**
     * Filtered headers based on search term
     */
    filteredHeaders() {
      if (!this.isSearchActive) {
        return this.pendingHeaders;
      }
      const term = this.searchTerm.toLowerCase().trim();
      return this.pendingHeaders.filter((header) => {
        const headerText = header.text || '';
        return headerText.toLowerCase().includes(term);
      });
    },

    /**
     * Get customizable headers (excluding action columns)
     */
    customizableHeaders() {
      const excludeValues = this.excludeColumns.map(v => v.toLowerCase());
      return this.pendingHeaders.filter(h => {
        if (!h || !h.value) return true;
        const itemValue = String(h.value).toLowerCase();
        const itemText = (h.text || '').toLowerCase();
        return !excludeValues.includes(itemValue) && !excludeValues.includes(itemText);
      });
    },

    /**
     * Determine the icon to show for Select All button based on current selection state
     * - All selected: checkbox-marked
     * - None selected: checkbox-blank-outline  
     * - Partial: minus-box-outline
     */
    selectAllIcon() {
      const total = this.customizableHeaders.length;
      if (total === 0) return 'mdi-checkbox-blank-outline';
      
      const visibleCount = this.customizableHeaders.filter(h => h.visible).length;
      
      if (visibleCount === 0) {
        return 'mdi-checkbox-blank-outline';
      } else if (visibleCount === total) {
        return 'mdi-checkbox-marked';
      } else {
        return 'mdi-minus-box-outline';
      }
    },

    /**
     * Determine the label for Select All button based on current selection state
     */
    selectAllLabel() {
      const total = this.customizableHeaders.length;
      if (total === 0) return 'Select all';
      
      const visibleCount = this.customizableHeaders.filter(h => h.visible).length;
      
      // If all are selected, show "Unselect all", otherwise show "Select all"
      return visibleCount === total ? 'Unselect all' : 'Select all';
    },

    /**
     * Check if all customizable headers are currently selected
     */
    isAllSelected() {
      const total = this.customizableHeaders.length;
      if (total === 0) return false;
      return this.customizableHeaders.filter(h => h.visible).length === total;
    },
  },

  watch: {
    /**
     * Reset state when menu opens/closes
     */
    isMenuOpen: {
      immediate: false,
      handler(isOpen) {
        if (isOpen) {
          // Clone headers when menu opens
          this.initializePendingHeaders();
        } else {
          // Reset search when menu closes
          this.resetState();
        }
      },
    },

    /**
     * Sync pending headers when prop changes
     */
    headers: {
      immediate: true,
      deep: true,
      handler() {
        this.initializePendingHeaders();
      },
    },
  },

  methods: {
    /**
     * Initialize pending headers with a deep clone of the headers prop
     */
    initializePendingHeaders() {
      this.pendingHeaders = JSON.parse(JSON.stringify(this.headers));
    },

    /**
     * Reset component state
     */
    resetState() {
      this.searchTerm = '';
    },

    /**
     * Toggle select all - if all selected, unselect all; otherwise select all
     */
    toggleSelectAll() {
      const newVisibility = !this.isAllSelected;
      this.pendingHeaders.forEach((header) => {
        if (!this.isExcludedColumn(header)) {
          this.$set(header, 'visible', newVisibility);
        }
      });
    },

    /**
     * Check if a column should be excluded from the list
     * @param {Object} item - Header item
     * @returns {Boolean}
     */
    isExcludedColumn(item) {
      if (!item || !item.value) return false;
      const excludeValues = this.excludeColumns.map(v => v.toLowerCase());
      const itemValue = String(item.value).toLowerCase();
      const itemText = (item.text || '').toLowerCase();
      return excludeValues.includes(itemValue) || excludeValues.includes(itemText);
    },

    /**
     * Get the visibility state of an item from pending headers
     * @param {Object} item - Header item
     * @returns {Boolean}
     */
    getItemVisibility(item) {
      const pendingItem = this.pendingHeaders.find(h => h.value === item.value);
      return pendingItem ? pendingItem.visible : false;
    },

    /**
     * Toggle column visibility in pending headers
     * @param {Object} item - Header item to toggle
     */
    toggleColumnVisibility(item) {
      const index = this.pendingHeaders.findIndex(h => h.value === item.value);
      if (index !== -1) {
        const newVisible = !this.pendingHeaders[index].visible;
        this.$set(this.pendingHeaders[index], 'visible', newVisible);
        
        // Emit toggle event for immediate feedback if needed
        this.$emit('toggle', {
          header: { ...this.pendingHeaders[index] },
          visible: newVisible,
        });
      }
    },

    /**
     * Handle Apply button click - applies changes locally
     */
    handleApply() {
      const updatedHeaders = JSON.parse(JSON.stringify(this.pendingHeaders));
      this.$emit('apply', updatedHeaders);
      this.closeMenu();
    },

    /**
     * Handle Save Columns button click - triggers persistence
     */
    handleSave() {
      const updatedHeaders = JSON.parse(JSON.stringify(this.pendingHeaders));
      this.$emit('save', {
        headers: updatedHeaders,
        pageName: this.pageName,
      });
      // Note: Menu close is handled by parent after successful save
    },

    /**
     * Close the menu programmatically
     */
    closeMenu() {
      this.isMenuOpen = false;
    },

    /**
     * Open the menu programmatically (exposed for parent usage)
     */
    openMenu() {
      this.isMenuOpen = true;
    },
  },

  /**
   * Reset state when component is destroyed
   */
  beforeDestroy() {
    this.resetState();
    this.pendingHeaders = [];
  },
};
</script>

<style lang="scss" scoped>
@import './CustomizeColumnMenu.scss';
</style>